import * as z from 'zod'

import type { AffiliateSocialLinkSchema, AffiliateSocialLinkSchemaPlatform } from '@/api/gen'

/**
 * The domain SSOT for a partner's social links. It mirrors the backend's
 * `social_links` contract (gto-wizard#2759) so the react-hook-form editor
 * validates the same rules the server enforces: a known-platform enum, a
 * custom-only label, an http(s) URL, a 20-link cap, and no duplicate
 * `(platform, url)`. The write body is a FULL-LIST replacement; `[]` clears.
 *
 * `socialLinksFormSchema` is what `zodResolver` consumes; `buildSocialLinks`
 * assembles the validated form values into the wire list (the same shape the
 * schema validates), and `socialLinksToForm` prefills the editor from a saved
 * list. The server 422 stays the authority — its field-scoped errors surface via
 * `setError`, everything else through the caller's inline alert.
 */

// Ordered for the platform Select. `satisfies` binds the list to the generated
// wire enum, so a BE platform rename breaks the build here rather than at runtime.
export const SOCIAL_PLATFORMS = [
  'youtube',
  'instagram',
  'x',
  'twitch',
  'tiktok',
  'discord',
  'kick',
  'facebook',
  'custom',
] as const satisfies readonly AffiliateSocialLinkSchemaPlatform[]

export type SocialPlatform = (typeof SOCIAL_PLATFORMS)[number]

export const isSocialPlatform = (value: unknown): value is SocialPlatform =>
  typeof value === 'string' && (SOCIAL_PLATFORMS as readonly string[]).includes(value)

// The `custom` platform is the only one whose label is partner-supplied; the
// rest render a fixed display name from this map.
export const PLATFORM_LABEL_IDS: Record<SocialPlatform, string> = {
  youtube: 'socialLinks.platform.youtube',
  instagram: 'socialLinks.platform.instagram',
  x: 'socialLinks.platform.x',
  twitch: 'socialLinks.platform.twitch',
  tiktok: 'socialLinks.platform.tiktok',
  discord: 'socialLinks.platform.discord',
  kick: 'socialLinks.platform.kick',
  facebook: 'socialLinks.platform.facebook',
  custom: 'socialLinks.platform.custom',
}

export const MAX_SOCIAL_LINKS = 20

// i18n message-id keys for client zod validation. The field renderer maps a
// `socialLinks.`-prefixed message through react-intl; a raw server 422 string
// renders verbatim (never parsed as an ICU template).
const V = {
  platformRequired: 'socialLinks.error.platformRequired',
  urlRequired: 'socialLinks.error.urlRequired',
  urlFormat: 'socialLinks.error.urlFormat',
  labelRequired: 'socialLinks.error.labelRequired',
  duplicate: 'socialLinks.error.duplicate',
} as const

/**
 * The flat shape react-hook-form holds per row. `platform` empty-string is the
 * "nothing picked yet" state the Select renders; `label` is only meaningful for
 * the `custom` platform (the field is hidden otherwise).
 */
export type SocialLinkFormRow = {
  platform: SocialPlatform | ''
  label: string
  url: string
}

export type SocialLinksFormValues = { links: SocialLinkFormRow[] }

// http(s) only, matching the BE. Uses the URL parser rather than a regex so
// `javascript:`/`mailto:`/relative inputs are rejected structurally. Exported so
// the read view can guard its anchor `href` against a non-http stored value.
export const isHttpUrl = (value: string): boolean => {
  try {
    const parsed = new URL(value)

    // Reject userinfo (`user[:pass]@host`): the real host is whatever follows the
    // `@`, so `https://youtube.com@evil.com` navigates to evil.com — a misdirection
    // link when the read view renders this as a live `href`. Mirrors the BE, which
    // 422s userinfo. A `@` in the path (e.g. `youtube.com/@handle`) is unaffected.
    if (parsed.username !== '' || parsed.password !== '') {
      return false
    }

    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

const rowSchema = z
  .object({
    platform: z.enum(SOCIAL_PLATFORMS).or(z.literal('')),
    label: z.string(),
    url: z.string(),
  })
  .superRefine((row, ctx) => {
    if (!isSocialPlatform(row.platform)) {
      ctx.addIssue({ code: 'custom', message: V.platformRequired, path: ['platform'] })
    }
    const url = row.url.trim()
    if (url === '') {
      ctx.addIssue({ code: 'custom', message: V.urlRequired, path: ['url'] })
    } else if (!isHttpUrl(url)) {
      ctx.addIssue({ code: 'custom', message: V.urlFormat, path: ['url'] })
    }
    // `custom` requires a non-empty label; a known platform must NOT carry one
    // (a label on a known platform is a BE 422). The editor hides the label field
    // for known platforms and the builder nulls it, so the only user-facing error
    // is the custom-needs-label case.
    if (row.platform === 'custom' && row.label.trim() === '') {
      ctx.addIssue({ code: 'custom', message: V.labelRequired, path: ['label'] })
    }
  })

export const socialLinksFormSchema = z
  .object({ links: z.array(rowSchema) })
  .superRefine((form, ctx) => {
    // Reject a duplicate (platform, url) — the same rule the BE enforces, on the
    // exact trimmed URL (the BE does not case-fold). Rows still missing a
    // platform/url are skipped; their own required-errors fire in the row schema
    // above. The 20-link cap is enforced by the disabled "Add link" button (and
    // the BE 422 backstop), so it needs no schema issue with no clean render row.
    const seen = new Set<string>()
    form.links.forEach((row, index) => {
      const url = row.url.trim()
      if (!isSocialPlatform(row.platform) || url === '') {
        return
      }
      const key = `${row.platform}::${url}`
      if (seen.has(key)) {
        ctx.addIssue({ code: 'custom', message: V.duplicate, path: ['links', index, 'url'] })
      } else {
        seen.add(key)
      }
    })
  })

/**
 * Assemble the wire list from validated form values. A valid form guarantees a
 * real platform on every row; the guard drops any un-picked row defensively (the
 * resolver blocks that case) and keeps the mapped type honest. Known platforms
 * send `label: null`; only `custom` carries the trimmed label.
 */
export const buildSocialLinks = (values: SocialLinksFormValues): AffiliateSocialLinkSchema[] =>
  values.links.flatMap((row) => {
    if (!isSocialPlatform(row.platform)) {
      return []
    }

    return [
      {
        platform: row.platform,
        label: row.platform === 'custom' ? row.label.trim() : null,
        url: row.url.trim(),
      },
    ]
  })

/** Prefill the editor from the saved wire list (custom keeps its label). */
export const socialLinksToForm = (
  links: readonly AffiliateSocialLinkSchema[] | undefined,
): SocialLinksFormValues => ({
  links: (links ?? []).map((link) => ({
    platform: link.platform,
    label: link.label ?? '',
    url: link.url,
  })),
})
